import { useEffect, useMemo, useRef, useState } from "react";
import actions from "../../actions";
import { useDispatch, useSelector } from "react-redux";
import { isEqual } from "lodash";
import { useParams, useLocation } from "react-router-dom";
import qs from "query-string";
import { tabs } from "./constants";
import recommendationActions from "../../../recommendations/actions";
import buildingActions from "../../../building/actions";
import projectActions from "../../../project/actions";
import { getAppliedFiltersCount, processDynamicFieldParams, stripEmptyListFilterParams } from "../../../../utils/filtersUtils";
import { isAppliedWildcardFilterEntry } from "../../../../utils/wildCardFilterUtils";
import { AppliedQueryFilters } from "../../../../config/utils";
import AssetAction from "../../../assets/actions";
import { isAdcOnlyProjectTypes } from "../../../project/projectTypes";
import { formatCapitalRequestSourceValue, getCapitalRequestLabel } from "../../../../utils/capitalRequestLabel";
import {
    ASSET_LIFECYCLE_OPTIONS,
    formatDashboardFilterOptionName,
    getDashboardFilterBandLabel,
    getDashboardFilterFieldLabel
} from "../../../../utils/dashboardFilterLabel";
import { recommendationCustomFieldBandKeyToFilterLabel } from "../../../../utils/recommendationBandUtils";
import { getEffectiveProjectIds, getSessionDefaultProjectId } from "../../../../utils/dashboardSession";
import {
    buildRecommendationDynamicFields,
    buildBuildingDynamicFields,
    buildPriorityFields,
    buildYearFields,
    enrichFilterField,
    getSearchKeyFromField,
    getWildcardStorageKey,
    isWildcardOnlyField
} from "../../filterFieldBuilders";
import { bands } from "./constants";
const DEFAULT_CSP_STATUS = ["recommendation"];
const HIDDEN_FILTER_PARAM_KEYS = ["csp_status"];

const isDefaultCspStatus = value => Array.isArray(value) && value.length === 1 && value[0] === DEFAULT_CSP_STATUS[0];

const normalizeDefaultFilters = filterParams => {
    const { update_fci, csp_status, ...rest } = filterParams || {};
    return {
        ...rest,
        ...(update_fci === false ? { update_fci } : {}),
        ...(Array.isArray(csp_status) && csp_status.length && !isDefaultCspStatus(csp_status) ? { csp_status } : {}),
        exclude_filters: filterParams?.exclude_filters || {}
    };
};


const useMyFilters = ({
    dashboardFilterParams,
    submitMasterFilter,
    onCancel,
    getListParams,
    isRecommendationTable,
    queries,
    saveFilterSubmit,
    isDashboard = false,
    isSmartChartFilter,
    isCoreReportFilters,
    isCR,
    // Optional Field Finder pre-fill (Otto's "Open the filter window", FTO-10). "" keeps today's behavior.
    initialFieldSearch = ""
}) => {
    const dispatch = useDispatch();
    const { masterFilterList, masterFilterCount, dropdownLoader, getDashboard = {}, filterFields, updateFilterName, defaultProject } = useSelector(
        state => state.dashboardReducer
    );
    const { recommendation_custom_fields = {} } = useSelector(state => state.recommendationsReducer?.dynamicFields || {});
    const { building_custom_field = {} } = useSelector(state => state.buildingReducer?.buildingDynamicFields || {});
    const { priorityElementsDropDownResponse = {} } = useSelector(state => state.recommendationsReducer || {});
    const [showBands, setShowBands] = useState({});
    const clientId = useSelector(state => state.projectReducer.getProjectByIdResponse?.client?.id);
    const miscellaneous = useSelector(state => state.projectReducer?.getProjectByIdResponse?.miscellaneous);
    const cspStatusOptions = useMemo(
        () => [
            { label: "Recommendation", id: "recommendation" },
            { label: getCapitalRequestLabel(miscellaneous), id: "capital_request" }
        ],
        [miscellaneous]
    );
    const getCspStatusLabels = statusIds =>
        cspStatusOptions.filter(option => (Array.isArray(statusIds) ? statusIds : []).includes(option.id)).map(option => option.label);
    const crLabel = getCapitalRequestLabel(miscellaneous);
    const isFmpUser = localStorage.getItem("fmp_user") === "yes";
    const labeledFilterFields = useMemo(
        () =>
            (filterFields || [])
                .filter(field => {
                    if (field.hide) return false;
                    if (field.requiresFmpUser && !isFmpUser) return false;
                    if (field.requiresShowEnergyBand && !miscellaneous?.show_energy_band) return false;
                    if (field.requiresShowWaterBand && !miscellaneous?.show_water_band) return false;
                    return true;
                })
                .map(field =>
                    enrichFilterField({
                        ...field,
                        label: getDashboardFilterFieldLabel(field, miscellaneous, isCR)
                    })
                ),
        [filterFields, miscellaneous, isCR, isFmpUser]
    );
    // Display-only option names; Redux masterFilterList ids/params stay unchanged.
    const displayMasterFilterList = useMemo(() => {
        if (!masterFilterList) return masterFilterList;
        return Object.fromEntries(
            Object.entries(masterFilterList).map(([key, list]) => {
                if (!Array.isArray(list)) return [key, list];
                return [
                    key,
                    list.map(opt => ({
                        ...opt,
                        name: formatDashboardFilterOptionName(opt.name, key, miscellaneous)
                    }))
                ];
            })
        );
    }, [masterFilterList, miscellaneous]);
    const projectTypes = useSelector(state => state.projectReducer.getProjectByIdResponse?.project_types);
    const isAdcOnlyProject = isAdcOnlyProjectTypes(projectTypes);
    const [showDropdown, setShowDropdown] = useState("");
    const [showWildcard, setShowWildcard] = useState("");
    const [showWildcardField, setWildcardField] = useState(!!Object.keys(dashboardFilterParams?.filters || {})?.length);
    const [filterData, setFilterData] = useState(normalizeDefaultFilters(dashboardFilterParams));
    const [filterName, setFilterName] = useState({ ...updateFilterName });

    const [initialFilterData, setInitialFilterData] = useState(normalizeDefaultFilters(dashboardFilterParams));

    const [activeTab, setActiveTab] = useState(Object.keys(tabs)[0]);
    const [showCancelConfirmation, setShowCancelConfirmation] = useState(false);
    const [yearFields, setYearFields] = useState([]);
    const [dynamicFields, setDynamicFields] = useState([]);
    const [buildingDynamicFields, setBuildingDynamicFields] = useState([]);
    const [priorityFields, setPriorityFields] = useState([]);
    const [fieldSearch, setFieldSearch] = useState(initialFieldSearch || "");
    const [showAppliedFilters, setShowAppliedFilters] = useState(false);
    const [isLoading, setLoading] = useState(false);
    const hasPageBeenRendered = useRef({});
    const emptyDivRef = useRef(null);
    const sectionRef = useRef(null);
    const [showSaveFilterModal, setShowSaveFilterModal] = useState(false);
    const path = useParams();
    const location = useLocation();
    const [favorites, setFavorites] = useState([]);
    const [showFavoriteRemoveConfirmation, setFavoriteRemoveConfirmation] = useState(false);
    const [selectedFavoriteItem, setSelectFavoriteItem] = useState("");
    const favFilterRef = useRef();
    const { favoriteFilters } = useSelector(state => state.assetReducer);

    const getDashboardProjectIds = (params = filterData) =>
        getEffectiveProjectIds(params, getSessionDefaultProjectId(defaultProject));

    const withDashboardProjectIds = params => {
        const projectIds = getDashboardProjectIds(params);
        return projectIds.length ? { ...params, project_ids: projectIds } : params;
    };
    useEffect(() => {
        if (dashboardFilterParams?.building_types?.length && isCoreReportFilters)
            setFilterData(normalizeDefaultFilters({ ...dashboardFilterParams, ...filterData }));
    }, [dashboardFilterParams?.building_types]);

    useEffect(() => {
        const timer1 = setTimeout(() => {
            getDynamicFields();
        }, 100);
        return () => {
            clearTimeout(timer1);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterData.project_ids, defaultProject]);

    const isFieldVisible = field => {
        const vt = field.rules?.visible_type ?? "both";
        if (vt === "both") return true;
        return isCR ? vt === "cr_only" : vt === "recommendation_only";
    };

    const tabRecommendationBandSet = useMemo(() => new Set(tabs.recommendation.bands), []);

    const dynamicBands = useMemo(
        () =>
            Object.entries(recommendation_custom_fields || {})
                .map(([band, fields]) => {
                    const filtered = fields.filter(isFieldVisible);
                    return [band, filtered];
                })
                .filter(([, fields]) => fields.length > 0)
                .map(([band]) => recommendationCustomFieldBandKeyToFilterLabel(band))
                .filter(label => label && !tabRecommendationBandSet.has(label)),
        [recommendation_custom_fields, isCR, tabRecommendationBandSet]
    );

    const buildingDynamicBands = useMemo(() => {
        const result = [];
        if (building_custom_field && Object.keys(building_custom_field)?.length) {
            Object.keys(building_custom_field).forEach(band => {
                result.push(band);
            });
        }
        return result;
    }, [building_custom_field]);

    const recommendationBands = useMemo(
        () => [
            bands.geo_hierarchy,
            bands.recommendation,
            bands.capital_spending_plan,
            bands.priority,
            bands.asset,
            ...(isFmpUser ? [bands.master_facility_plan] : []),
            bands.additional_details,
            ...(miscellaneous?.show_energy_band ? [bands.energy] : []),
            ...(miscellaneous?.show_water_band ? [bands.water] : []),
            ...dynamicBands
        ],
        [isFmpUser, miscellaneous?.show_energy_band, miscellaneous?.show_water_band, dynamicBands]
    );

    useEffect(() => {
        const Filterstabs = {
            ...tabs,
            building: { ...tabs.building, bands: [...tabs.building.bands, ...buildingDynamicBands] },
            recommendation: {
                ...tabs.recommendation,
                bands: recommendationBands
            },
            ...((isDashboard || isRecommendationTable) && {
                favorites: {
                    label: "Favorites",
                    bands: []
                }
            })
        };
        if (fieldSearch && fieldSearch?.toString()?.trim()?.length) {
            const fields = labeledFilterFields?.filter(field =>
                field?.label?.toString()?.toLowerCase()?.includes(fieldSearch?.toString()?.toLowerCase())
            );
            const currentTab = getActiveTabWithData(activeTab, Filterstabs, fields);
            if (currentTab !== activeTab) {
                setActiveTab(currentTab);
            }
        }
    }, [fieldSearch, recommendationBands, buildingDynamicBands, labeledFilterFields, activeTab, isDashboard, isRecommendationTable]);

    useEffect(() => {
        if (hasPageBeenRendered.current["effect1"]) {
            if (recommendation_custom_fields && Object.keys(recommendation_custom_fields)?.length) {
                setDynamicFields(buildRecommendationDynamicFields(recommendation_custom_fields, isCR));
            }
        }
        hasPageBeenRendered.current["effect1"] = true;
    }, [recommendation_custom_fields, isCR]);

    useEffect(() => {
        if (hasPageBeenRendered.current["effect1"]) {
            if (building_custom_field && Object.keys(building_custom_field)?.length) {
                setBuildingDynamicFields(buildBuildingDynamicFields(building_custom_field));
            }
        }
        hasPageBeenRendered.current["effect1"] = true;
    }, [building_custom_field]);

    useEffect(() => {
        if (hasPageBeenRendered.current["effect1"]) {
            setPriorityFields(buildPriorityFields(priorityElementsDropDownResponse));
        }
        hasPageBeenRendered.current["effect1"] = true;
    }, [priorityElementsDropDownResponse]);

    useEffect(() => {
        const timer2 = setTimeout(() => {
            const { start, end } = getDashboard?.years || {};
            if (start && end) {
                setYearFields(buildYearFields(getDashboard?.years));
            }
        }, 100);

        return () => {
            clearTimeout(timer2);
        };
    }, [getDashboard]);

    useEffect(() => {
        if (hasPageBeenRendered.current["effect3"]) {
            dispatch(actions.updateMasterFilter([...yearFields, ...dynamicFields, ...buildingDynamicFields, ...priorityFields]));
        }
        hasPageBeenRendered.current["effect3"] = true;
    }, [dispatch, yearFields, dynamicFields, buildingDynamicFields, priorityFields]);

    useEffect(() => {
        if (hasPageBeenRendered.current["effect4"]) {
            if (emptyDivRef.current && emptyDivRef.current.classList?.contains("d-block")) {
                emptyDivRef.current.classList.remove("d-block");
                emptyDivRef.current.classList.add("d-none");
                if (sectionRef.current) {
                    sectionRef.current.scrollIntoView({ behavior: "smooth" });
                }
            }
        }
        hasPageBeenRendered.current["effect4"] = true;
    }, [activeTab]);

    useEffect(() => {
        if (clientId) dispatch(buildingActions.getBuildingDynamicFields({ client_id: clientId }));
    }, [clientId]);
    useEffect(() => {
        if (favFilterRef.current && (isDashboard || isRecommendationTable)) {
            saveFavorites();
        }
    }, [favorites]);

    useEffect(() => {
        if (isDashboard || isRecommendationTable) {
            const params = {
                user_id: localStorage.getItem("userId"),
                filter_key: isDashboard ? "1" : "2"
            };
            dispatch(AssetAction.getFavorites(params));
        }
    }, []);
    useEffect(() => {
        if (isDashboard || isRecommendationTable) {
            setFavorites(favoriteFilters?.data?.properties || []);
            // setActiveTab(favoriteFilters?.data?.properties?.length > 0 ? "favorites" : Object.keys(tabs)[0]);
        }
    }, [favoriteFilters]);

    const handleFilterView = async (item, dropdownRef, isWildCardFilterMode = false, isSmartChartSubmit = false, searchTerm = null) => {
        const isServerSearch = searchTerm !== null;
        const {
            key,
            paramKey,
            options = [],
            searchKey,
            getListKey,
            isRecommendationField,
            isDynamicField,
            isBoolean,
            inlineOptions,
            useRecommendationGetList
        } = item || {};
        const isOpeningFilter =
            dropdownRef?.current &&
            ((isWildCardFilterMode && showWildcard !== key) || (!isWildCardFilterMode && showDropdown !== key));
        if (isOpeningFilter) {
            if (emptyDivRef.current) {
                emptyDivRef.current.classList.remove("d-none");
                emptyDivRef.current.classList.add("d-block");
            }
            dropdownRef.current.scrollIntoView({ behavior: "smooth" });
        }
        if (isWildCardFilterMode) {
            setShowWildcard(showWildcard === key ? "" : key);
            setShowDropdown("");
        } else if (!isSmartChartSubmit && !isServerSearch) {
            setShowDropdown(showDropdown === key ? "" : key);
            setShowWildcard("");
        }
        if (
            ((!options?.length || isServerSearch) && (key !== showDropdown || isSmartChartSubmit || isServerSearch) && !isWildCardFilterMode) ||
            isBoolean
        ) {
            let { [paramKey]: omit, ...rest } = { ...filterData };
            if (key === "year") {
                const { start_year: omitStart, end_year: omitEnd, ...restQuery } = rest;
                rest = restQuery;
            }
            let remainingFilter = { ...rest };

            if (remainingFilter.exclude_filters) {
                const { [paramKey]: omit, ...restExcludeFilters } = remainingFilter.exclude_filters;
                remainingFilter = { ...rest, exclude_filters: restExcludeFilters };
            }
            let resultValue = {};
            let removingexcludeFilters = { ...rest };
            if (isDashboard) {
                const result = await AppliedQueryFilters(remainingFilter, queries);
                const {
                    [paramKey]: omittedValue,
                    exclude_filters: { [paramKey]: excludedValue, ...remainingExcludeFilters },
                    ...remainingResult
                } = result;

                resultValue = withDashboardProjectIds({
                    ...remainingResult,
                    exclude_filters: remainingExcludeFilters
                });
            } else {
                let results = rest;
                if (isSmartChartFilter || isCoreReportFilters) {
                    results = processDynamicFieldParams(rest);
                }
                const { exclude_filters: omit, ...restOfFilters } = { ...results };
                removingexcludeFilters = {
                    ...restOfFilters
                };
            }

            const { [paramKey]: omitList, ...restListParams } = getListParams;
            const searchFilterKey = searchKey || getSearchKeyFromField(item);
            const trimmedSearch = (searchTerm || "").trim();
            const searchFilters =
                trimmedSearch && searchFilterKey ? { search_filters: { [searchFilterKey]: { key: trimmedSearch } } } : {};
            let params = {
                ...searchFilters,
                searchKey,
                field: isBoolean
                    ? ["Yes", "No"]
                    : isDynamicField && inlineOptions?.length
                      ? inlineOptions.map(o => o.id)
                      : getListKey,
                ...restListParams,
                ...(isDashboard ? resultValue : removingexcludeFilters)
            };

            if (["sub_system", "additions"].includes(key) && isRecommendationTable) params.limit = 500;
            else if (["sites", "buildings", "floors"].includes(key) && isRecommendationTable) params.limit = 1000;

            if (isDynamicField) {
                params.is_dynamic = isDynamicField;
            }
            if (isRecommendationTable || useRecommendationGetList) {
                dispatch(actions.getMasterFilter(params, key, isRecommendationField, true, isServerSearch));
            } else if (isRecommendationField) {
                dispatch(actions.getMasterFilter(params, key, isRecommendationField, false, isServerSearch));
            } else {
                const baseParams = isDashboard ? resultValue : removingexcludeFilters;
                dispatch(actions.getMasterFilter({ ...baseParams, ...searchFilters }, key, false, false, isServerSearch));
            }
        }
    };
    const getActiveTabWithData = (activeTab, tabs, filterFields) => {
        const hasData = tabKey => tabs[tabKey]?.bands.some(band => filterFields?.some(field => field?.band === band));
        let favFilters = [];
        favFilters = filterFields?.filter(field => favorites?.includes(field?.key))?.map(fav => ({ ...fav, isFavorite: true }));
        favFilters = favFilters?.filter(field => field?.label?.toString()?.toLowerCase()?.includes(fieldSearch?.toString()?.toLowerCase()));
        if (activeTab === "favorites" && fieldSearch && favFilters?.length > 0) {
            return activeTab;
        }
        return hasData(activeTab) ? activeTab : Object.keys(tabs).find(hasData) || activeTab;
    };

    const handleFilterUpdate = (item, data, exclude) => {
        setShowDropdown("");
        setShowWildcard("");
        let updatedData = data;
        if (item.key !== "year" && data?.length) {
            let options = displayMasterFilterList[item.key];
            if (item.key === "building_types") {
                updatedData = data.filter(item => options.findIndex(opt => opt.name === item) !== -1);
            } else {
                updatedData = data.filter(item => options.findIndex(opt => opt.id === item) !== -1);
            }
        }
        if (item.key === "year") {
            setFilterData({ ...filterData, ...data });
            setFilterName({ ...filterName, ...data });
        } else if (item.key === "projects") {
            let newFilterData = { ...filterData };
            labeledFilterFields
                .filter(field => field.isDynamicField)
                .forEach(field => {
                    if (newFilterData.hasOwnProperty(field.paramKey)) {
                        delete newFilterData[field.paramKey];
                    }
                });
            setFilterData({
                ...newFilterData,
                ...(exclude
                    ? { exclude_filters: { ...newFilterData.exclude_filters, [item.paramKey]: updatedData } }
                    : { [item.paramKey]: updatedData })
            });
            setFilterName(prevFilterData => ({
                ...prevFilterData,
                ...(exclude
                    ? {
                          exclude_filters: {
                              ...prevFilterData.exclude_filters,
                              [item.label]:
                                  item.key === "building_types"
                                      ? displayMasterFilterList[item.key].filter(entry => new Set(data).has(entry.name)).map(entry => entry.name)
                                      : displayMasterFilterList[item.key].filter(entry => new Set(data).has(entry.id)).map(entry => entry.name)
                          }
                      }
                    : {
                          [item.key === "building_types" ? "Building Types" : item.label]:
                              item.key === "building_types"
                                  ? displayMasterFilterList[item.key].filter(entry => new Set(data).has(entry.name)).map(entry => entry.name)
                                  : displayMasterFilterList[item.key].filter(entry => new Set(data).has(entry.id)).map(entry => entry.name)
                      })
            }));
        } else {
            if (exclude) {
                if (updatedData?.length > 0) {
                    setFilterData(prevFilterData => ({
                        ...prevFilterData,
                        [item.paramKey]: [],
                        exclude_filters: {
                            ...prevFilterData?.exclude_filters,
                            [item.paramKey]: updatedData
                        }
                    }));
                    setFilterName(prevFilterData => ({
                        ...prevFilterData,
                        [item.key === "building_types" ? "Building Types" : item.label]: [],
                        exclude_filters: {
                            ...prevFilterData?.exclude_filters,
                            [item.key === "building_types" ? "Building Types" : item.label]:
                                item.key === "building_types"
                                    ? displayMasterFilterList[item.key].filter(entry => new Set(data).has(entry.name)).map(entry => entry.name)
                                    : displayMasterFilterList[item.key].filter(entry => new Set(data).has(entry.id)).map(entry => entry.name)
                        }
                    }));
                } else {
                    setFilterData(prevFilterData => {
                        const newFilterData = { ...prevFilterData };
                        delete newFilterData[item.paramKey];
                        if (newFilterData.exclude_filters) {
                            delete newFilterData.exclude_filters[item.paramKey];
                        }
                        return newFilterData;
                    });
                    setFilterName(prevFilterData => {
                        const newFilterData = { ...prevFilterData };
                        delete newFilterData[item.key === "building_types" ? "Building Types" : item.label];
                        if (newFilterData.exclude_filters) {
                            delete newFilterData.exclude_filters[item.key === "building_types" ? "Building Types" : item.label];
                        }
                        return newFilterData;
                    });
                }
            } else {
                if (updatedData?.length > 0) {
                    const nextExclude = { ...(filterData?.exclude_filters || {}) };
                    delete nextExclude[item.paramKey];
                    const { exclude_filters: _omitExclude, ...restFilterData } = filterData || {};
                    setFilterData({
                        ...restFilterData,
                        [item.paramKey]: updatedData,
                        ...(Object.keys(nextExclude).length ? { exclude_filters: nextExclude } : {})
                    });
                    const labelKey = item.key === "building_types" ? "Building Types" : item.label;
                    const nextExcludeNames = { ...(filterName?.exclude_filters || {}) };
                    delete nextExcludeNames[labelKey];
                    const { exclude_filters: _omitExcludeNames, ...restFilterName } = filterName || {};
                    setFilterName({
                        ...restFilterName,
                        [labelKey]:
                            item.key === "building_types"
                                ? displayMasterFilterList[item.key].filter(entry => new Set(data).has(entry.name)).map(entry => entry.name)
                                : displayMasterFilterList[item.key].filter(entry => new Set(data).has(entry.id)).map(entry => entry.name),
                        ...(Object.keys(nextExcludeNames).length ? { exclude_filters: nextExcludeNames } : {})
                    });
                } else {
                    setFilterData(prevFilterData => {
                        const newFilterData = { ...prevFilterData };
                        delete newFilterData[item.paramKey];
                        if (newFilterData.exclude_filters) {
                            delete newFilterData.exclude_filters[item.paramKey];
                            if (Object.keys(newFilterData.exclude_filters).length === 0) {
                                delete newFilterData.exclude_filters;
                            }
                        }
                        return newFilterData;
                    });
                    setFilterName(prevFilterName => {
                        const labelKey = item.key === "building_types" ? "Building Types" : item.label;
                        const newFilterName = { ...prevFilterName };
                        delete newFilterName[labelKey];
                        if (newFilterName.exclude_filters) {
                            delete newFilterName.exclude_filters[labelKey];
                            if (Object.keys(newFilterName.exclude_filters).length === 0) {
                                delete newFilterName.exclude_filters;
                            }
                        }
                        return newFilterName;
                    });
                }
            }
        }
    };
    const handleBandClick = (key, value) => {
        setShowBands({ ...showBands, [key]: value });
    };
    const handleSubmit = isSmartChart => {
        let { start_year, end_year, update_fci, project_cost, ...updatedFilterData } = { ...filterData };
        const filteredWildCardData = Object.entries(updatedFilterData?.filters || {}).reduce((result, [key, elem]) => {
            if (isAppliedWildcardFilterEntry(elem)) {
                result[key] = elem;
            }
            return result;
        }, {});
        const filteredTopNData = Object.entries(updatedFilterData?.top_n || {}).reduce((result, [key, elem]) => {
            if (elem?.trim().length) {
                result[key] = elem;
            }
            return result;
        }, {});
        if (Object.keys(filteredWildCardData).length > 0) {
            updatedFilterData.filters = filteredWildCardData;
        } else {
            delete updatedFilterData.filters;
        }

        if (Object.keys(filteredTopNData).length > 0) {
            updatedFilterData.top_n = filteredTopNData;
        }
        if (start_year && end_year) {
            updatedFilterData.start_year = start_year;
            updatedFilterData.end_year = end_year;
        }
        if ("update_fci" in filterData) {
            updatedFilterData.update_fci = update_fci ?? true;
        }
        if (filterData.project_cost) {
            updatedFilterData.project_cost = true;
        }
        updatedFilterData = stripEmptyListFilterParams(updatedFilterData);
        if (isSmartChart) {
            Object?.keys(updatedFilterData)?.map(async paramKeys => {
                if (
                    paramKeys !== "filters" &&
                    paramKeys !== "project_ids" &&
                    paramKeys !== "client_ids" &&
                    paramKeys !== "exclude_filters" &&
                    paramKeys !== "end_year" &&
                    paramKeys !== "start_year" &&
                    paramKeys !== "update_fci" &&
                    paramKeys !== "project_cost" &&
                    paramKeys !== "csp_status"
                ) {
                    const filterDetail = labeledFilterFields?.find(({ paramKey }) => paramKey === paramKeys);
                    await handleFilterView(filterDetail, null, false, true);
                }
            });
        } else {
            setInitialFilterData(updatedFilterData);
            onCancel();
        }
        submitMasterFilter(updatedFilterData, filterName);
    };
    const handleSaveFilerModal = () => {
        setShowSaveFilterModal(true);
    };
    const handleSaveFilter = filterSaveData => {
        let updatedFilterData = { ...filterData };
        const filteredWildCardData = Object.entries(updatedFilterData?.filters || {}).reduce((result, [key, elem]) => {
            if (isAppliedWildcardFilterEntry(elem)) {
                result[key] = elem;
            }
            return result;
        }, {});
        const filteredTopNData = Object.entries(updatedFilterData?.top_n || {}).reduce((result, [key, elem]) => {
            if (elem?.trim().length) {
                result[key] = elem;
            }
            return result;
        }, {});
        if (Object.keys(filteredWildCardData).length) {
            updatedFilterData.filters = filteredWildCardData;
        }
        if (Object.keys(filteredTopNData).length) {
            updatedFilterData.top_n = filteredTopNData;
        }
        updatedFilterData = stripEmptyListFilterParams(updatedFilterData);
        setShowSaveFilterModal(false);
        saveFilterSubmit({ ...filterSaveData, query: updatedFilterData, query_label: filterName });
        onCancel();
    };

    const isDirty = !isEqual(filterData, initialFilterData);

    const handleCancel = () => {
        isDirty ? setShowCancelConfirmation(true) : onCancel();
    };

    const onCancelConfirm = () => {
        onCancel();
    };

    const getDynamicFields = async () => {
        setLoading(true);
        let project_id = null;
        if (!isRecommendationTable) {
            project_id = getDashboardProjectIds()[0] || null;
        } else {
            const search = location?.search;
            const query = qs.parse(search);

            project_id = query.pid || getListParams?.project_id || path.id || "";
        }
        await Promise.all([
            dispatch(recommendationActions.getDynamicFields({ project_id })),
            project_id && dispatch(projectActions.getProjectById(project_id)),
            dispatch(recommendationActions.getPriorityElementDropDownData(project_id))
        ]);

        setLoading(false);
    };

    const clearAllFilters = isSmartChartFilter => {
        if (isSmartChartFilter) {
            const filteredObject = Object.keys(filterData).reduce((acc, key) => {
                if (key === "project_ids" || key === "client_ids") {
                    acc[key] = filterData[key];
                }
                return acc;
            }, {});
            setFilterData(normalizeDefaultFilters(filteredObject));
            // submitMasterFilter(filteredObject);
        } else {
            setFilterData({ exclude_filters: {} });
            setFilterName({ exclude_filters: {} });
        }
    };

    const getFilterCountByBand = (band, isSmartChartFilter = false, tabKey = activeTab) => {
        if (!(filterData && Object.keys(filterData)?.length)) {
            return 0;
        }
        let bandFilterCount = 0;
        let bandFields = [];
        if (isSmartChartFilter) {
            bandFields = labeledFilterFields.filter(
                elem =>
                    elem.band === band &&
                    elem.paramKey !== "client_ids" &&
                    !HIDDEN_FILTER_PARAM_KEYS.includes(elem.paramKey) &&
                    (elem.isDynamicField ? elem.tab === tabKey : true)
            );
        } else {
            bandFields =
                labeledFilterFields.filter(
                    elem =>
                        elem.band === band &&
                        !HIDDEN_FILTER_PARAM_KEYS.includes(elem.paramKey) &&
                        (elem.isDynamicField ? elem.tab === tabKey : true)
                ) || [];
        }
        const bandParamKeys = bandFields.map(field => field.paramKey);
        const wildcardParamKeys = bandFields.map(field => field.wildcardParamKey).filter(Boolean);
        bandFilterCount = Object.keys(filterData).filter(key => bandParamKeys.includes(key) && filterData[key]?.length).length;

        if (bandParamKeys.includes("year") && (filterData.start_year || filterData.end_year)) {
            bandFilterCount += 1;
        }
        if (filterData.filters && Object.keys(filterData.filters).length) {
            Object.entries(filterData.filters).forEach(([key, value]) => {
                if ((bandParamKeys.includes(key) || wildcardParamKeys.includes(key)) && isAppliedWildcardFilterEntry(value)) {
                    bandFilterCount += 1;
                }
            });
        }
        if (filterData.top_n && Object.keys(filterData.top_n).length) {
            Object.entries(filterData?.top_n).forEach(([key, value]) => {
                if (bandParamKeys?.includes(key) && value) {
                    bandFilterCount += 1;
                }
            });
        }

        return bandFilterCount;
    };

    const getFilterCountByTab = (tabKey, isSmartChartFilter = false) => {
        if (tabKey === "favorites") {
            return 0;
        }
        const filterTabs = {
            ...tabs,
            building: { ...tabs.building, bands: [...tabs.building.bands, ...buildingDynamicBands] },
            recommendation: {
                ...tabs.recommendation,
                bands: recommendationBands
            }
        };
        const tabBands = filterTabs[tabKey]?.bands || [];
        return tabBands.reduce((total, band) => total + getFilterCountByBand(band, isSmartChartFilter, tabKey), 0);
    };

    const setWildCardFilter = (elem, data, checked) => {
        if (data.filters?.[0] === "top_n") {
            setTopNFilter(elem, data);
        } else {
            const wildcardKey = getWildcardStorageKey(elem);
            let currentFilter = { ...filterData?.filters } || {};
            let currentFilterName = { ...filterName?.filters } || {};
            if (data?.filters?.[0] === "in_between" && !(data?.key?.from && data.key?.to)) {
                return;
            } else if (Object.keys(data?.key ?? {}).length && (!data?.filters?.length || data.filters[0] === "")) {
                if (currentFilter?.hasOwnProperty(wildcardKey)) {
                    delete currentFilter[wildcardKey];
                    delete currentFilterName[elem.label];
                }
            } else if (data?.filters?.[0] !== "in_between" && !data?.key?.trim()?.length && data?.filters[0] === "") {
                if (currentFilter?.hasOwnProperty(wildcardKey)) {
                    delete currentFilter[wildcardKey];
                    delete currentFilterName[elem.label];
                }
            } else {
                currentFilter = {
                    ...currentFilter,
                    [wildcardKey]: data
                };
                currentFilterName = {
                    ...currentFilterName,
                    [elem.label]: data
                };
            }
            setFilterData({ ...filterData, filters: currentFilter });
            setFilterName({ ...filterName, filters: currentFilterName });
        }
    };

    const setTopNFilter = (elem, data) => {
        let currentFilter = filterData?.top_n || {};
        let currentFilterName = filterData?.top_n || {};
        if (data?.key) {
            currentFilter = {
                ...currentFilter,
                [elem.paramKey]: data.key
            };
            currentFilterName = {
                ...currentFilterName,
                [elem.label]: data.key
            };
        } else {
            if (currentFilter?.hasOwnProperty(elem.paramKey)) {
                delete currentFilter[elem.paramKey];
                delete currentFilterName[elem.label];
            }
        }
        setFilterData({ ...filterData, top_n: currentFilter });
        setFilterName({ ...filterName, top_n: currentFilterName });
    };

    const getTotalFilterCount = () => {
        let filterCount = 0;
        filterCount = getAppliedFiltersCount(filterData);
        filterCount += queries?.length;
        return filterCount;
    };
    const toggleFavorite = field => {
        if (favorites.includes(field)) {
            if (activeTab === "favorites") {
                setFavoriteRemoveConfirmation(true);
                setSelectFavoriteItem(field);
            } else {
                setFavorites(favorites.filter(item => item !== field));
            }
        } else {
            setFavorites([...favorites, field]);
        }
        favFilterRef.current = true;
    };
    const saveFavorites = () => {
        let params = {
            user_id: localStorage.getItem("userId"),
            properties: JSON.stringify(favorites),
            filter_key: isDashboard ? "1" : "2"
        };
        AssetAction.saveFavorites(params);
    };

    const onFavoriteRemoveConfirmation = () => {
        setFavoriteRemoveConfirmation(false);
        if (favorites.includes(selectedFavoriteItem)) {
            setFavorites(favorites.filter(item => item !== selectedFavoriteItem));
            favFilterRef.current = true;
        }
    };

    const toggleStaticFci = checked => {
        if (checked) {
            const newFilterData = { ...filterData };
            delete newFilterData.update_fci;
            const newFilterName = { ...filterName };
            delete newFilterName.update_fci;
            setFilterData(newFilterData);
            setFilterName(newFilterName);
        } else {
            setFilterData({ ...filterData, update_fci: false });
            setFilterName({ ...filterName, update_fci: false });
        }
    };

    const toggleProjectCost = checked => {
        if (checked) {
            setFilterData({ ...filterData, project_cost: true });
            setFilterName({ ...filterName, project_cost: true });
        } else {
            const { project_cost, ...newFilterData } = filterData;
            const { project_cost: _, ...newFilterName } = filterName;
            setFilterData(newFilterData);
            setFilterName(newFilterName);
        }
    };

    const toggleAssetLifecycle = option => {
        const newExcludeFilters = { ...(filterData?.exclude_filters || {}) };
        delete newExcludeFilters.recommendation_type;
        const newExcludeFilterNames = { ...(filterName?.exclude_filters || {}) };
        const recommendationTypeFilterLabel = getDashboardFilterFieldLabel(
            { key: "recommendation_type", label: "Recommendation Type" },
            miscellaneous,
            isCR
        );
        delete newExcludeFilterNames[recommendationTypeFilterLabel];

        setFilterData({
            ...filterData,
            recommendation_type: [option.value],
            exclude_filters: newExcludeFilters
        });
        setFilterName({
            ...filterName,
            [recommendationTypeFilterLabel]: [option.name],
            exclude_filters: newExcludeFilterNames
        });
    };

    const toggleCspStatus = statusId => {
        const currentCspStatus = Array.isArray(filterData?.csp_status) ? filterData.csp_status : DEFAULT_CSP_STATUS;
        const nextCspStatus = currentCspStatus.includes(statusId)
            ? currentCspStatus.filter(id => id !== statusId)
            : [...currentCspStatus, statusId];

        if (!nextCspStatus.length) {
            return;
        }

        if (isDefaultCspStatus(nextCspStatus)) {
            const newFilterData = { ...filterData };
            delete newFilterData.csp_status;
            const newFilterName = { ...filterName };
            delete newFilterName["CSP Status"];
            setFilterData(newFilterData);
            setFilterName(newFilterName);
            return;
        }

        setFilterData({ ...filterData, csp_status: nextCspStatus });
        setFilterName({
            ...filterName,
            "CSP Status": getCspStatusLabels(nextCspStatus)
        });
    };

    return {
        showBands,
        showDropdown,
        handleFilterView,
        dashboardFilterParams,
        masterFilterList: displayMasterFilterList,
        masterFilterCount,
        handleBandClick,
        dropdownLoader,
        filterData,
        filterName,
        handleFilterUpdate,
        isDirty,
        handleSubmit,
        activeTab,
        setActiveTab,
        showCancelConfirmation,
        onCancelConfirm,
        setShowCancelConfirmation,
        handleCancel,
        dynamicBands,
        buildingDynamicBands,
        recommendationBands,
        filterFields: labeledFilterFields,
        clearAllFilters,
        getFilterCountByBand,
        getFilterCountByTab,
        setShowDropdown,
        setWildCardFilter,
        showWildcard,
        setShowWildcard,
        showWildcardField,
        setWildcardField,
        isLoading,
        fieldSearch,
        setFieldSearch,
        showAppliedFilters,
        setShowAppliedFilters,
        totalFilterCount: getTotalFilterCount(),
        emptyDivRef,
        sectionRef,
        showSaveFilterModal,
        setShowSaveFilterModal,
        handleSaveFilerModal,
        handleSaveFilter,
        toggleFavorite,
        saveFavorites,
        onFavoriteRemoveConfirmation,
        showFavoriteRemoveConfirmation,
        favorites,
        setFavoriteRemoveConfirmation,
        toggleStaticFci,
        toggleProjectCost,
        toggleAssetLifecycle,
        toggleCspStatus,
        cspStatusOptions,
        assetLifecycleOptions: ASSET_LIFECYCLE_OPTIONS,
        isAdcOnlyProject,
        crLabel,
        getDashboardFilterBandLabel: band => getDashboardFilterBandLabel(band, miscellaneous, isCR)
    };
};
export default useMyFilters;
